<template>
  <div
    id="accountPreferences"
    ref="accountPreferencesSection"
    data-test="account-preferences"
  >
    <div style="display: flex; align-items: center">
      <h2 class="account-card-title">Preferences</h2>
      <div style="width: 25px; height: 18px; padding-left: 8px">
        <Pencil
          :fill="mainColor"
          class="cursor-pointer"
          @click="editPreferences"
          v-if="!edit.preferences"
          data-test="edit-button"
        />
      </div>
    </div>
    <Card
      class="account-card-info card-style"
      v-if="!edit.preferences"
      data-test="preferences-view"
    >
      <template #content>
        <div>
          <div class="field-title">Categories</div>
          <div
            v-for="(category, index) in donorStore.donorPreferences.categories"
            :key="index"
            data-test="category-item"
          >
            <a
              :href="`${donorStore.domain.replace(/\/$/, '')}/organizations?loq_category=${category.customLookups_id}`"
            >
              {{ category.longValue }}
            </a>
          </div>
        </div>

        <div>
          <div class="field-title">Organization Size</div>
          <div
            v-for="(orgSize, index) in donorStore.donorPreferences.orgSizes"
            :key="index"
            data-test="org-size-item"
          >
            {{ orgSize.longValue }}
          </div>
        </div>

        <div>
          <div class="field-title">Areas of Impact</div>
          <div
            v-for="(areaOfImpact, index) in donorStore.donorPreferences.areasOfImpact"
            :key="index"
            data-test="area-of-impact-item"
          >
            <a
              :href="`${donorStore.domain.replace(/\/$/, '')}/organizations?location=${areaOfImpact.customLookups_id}`"
            >
              {{ areaOfImpact.longValue }}
            </a>
          </div>
        </div>
      </template>
    </Card>
    <Card
      class="account-card-edit-info-grid card-style"
      v-if="edit.preferences"
      data-test="preferences-edit"
    >
      <template #content>
        <AccountPreferencesForm
          :localPreferences="localPreferences"
          @update:localPreferences="(newPreferences) => (localPreferences = newPreferences)"
        />
        <div style="display: flex; justify-content: space-between; grid-column: span 2">
          <Button
            class="p-button-text"
            :style="{ color: donorStore.mainColor }"
            @click="cancelPreferences"
            data-test="cancel-button"
          >
            Cancel
          </Button>
          <Button
            class="p-button"
            :style="{ color: getContrastYIQ(donorStore.mainColor) }"
            @click="updatePreferences()"
            data-test="save-button"
          >
            Save Changes
          </Button>
        </div>
      </template>
    </Card>
  </div>
</template>
<script setup>
import { ref, onMounted } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import Pencil from '@/assets/Pencil.vue';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import AccountPreferencesForm from '@/components/settings/AccountPreferencesForm.vue';

const donorStore = useDonorStore();

donorStore.fetchHostPreferences();
const mainColor = donorStore.mainColor;
const donorId = donorStore.donorInfo.donorAccounts_id;

const edit = ref({
  preferences: false,
});
const accountPreferencesSection = ref(null);
const editPreferences = () => {
  edit.value.preferences = true;

  if (accountPreferencesSection.value) {
    accountPreferencesSection.value.scrollIntoView(true);
  }
};

let localPreferences = ref({
  categories: [],
  orgSizes: [],
  areasOfImpact: [],
});

let initialPreferences = JSON.parse(JSON.stringify(localPreferences.value));

function toggleSelect(tag, type) {
  if (!Array.isArray(localPreferences.value[type])) {
    localPreferences.value[type] = [];
  }

  const index = localPreferences.value[type].findIndex(
    (localTag) => localTag.customLookups_id === tag.customLookups_id,
  );

  if (index >= 0) {
    localPreferences.value[type].splice(index, 1);
  } else {
    localPreferences.value[type].push(tag);
  }
}

const updatePreferences = () => {
  // Update the donor store with a deep copy of local preferences
  donorStore.donorPreferences.categories = JSON.parse(JSON.stringify(localPreferences.value.categories));
  donorStore.donorPreferences.orgSizes = JSON.parse(JSON.stringify(localPreferences.value.orgSizes));
  donorStore.donorPreferences.areasOfImpact = JSON.parse(JSON.stringify(localPreferences.value.areasOfImpact));

  // Update donor account in DB
  donorStore.updateDonorAccount(donorId, {
    preferences: {
      categories: JSON.parse(JSON.stringify(localPreferences.value.categories)),
      orgSizes: JSON.parse(JSON.stringify(localPreferences.value.orgSizes)),
      areasOfImpact: JSON.parse(JSON.stringify(localPreferences.value.areasOfImpact)),
    },
  });

  // Update initialPreferences with the saved preferences
  initialPreferences = JSON.parse(JSON.stringify(localPreferences.value));

  edit.value.preferences = false;
};

const cancelPreferences = () => {
  edit.value.preferences = false;
  localPreferences.value = JSON.parse(JSON.stringify(initialPreferences));
};

onMounted(() => {
  if (donorStore.donorPreferences) {
    localPreferences.value.categories = JSON.parse(JSON.stringify(donorStore.donorPreferences.categories)) || [];
    localPreferences.value.orgSizes = JSON.parse(JSON.stringify(donorStore.donorPreferences.orgSizes)) || [];
    localPreferences.value.areasOfImpact = JSON.parse(JSON.stringify(donorStore.donorPreferences.areasOfImpact)) || [];

    // Update initialPreferences with the current preferences
    initialPreferences = JSON.parse(JSON.stringify(localPreferences.value));
  }
});
</script>
